/**
 * CreateTicketCatalogModal - 수강권 카탈로그를 새로 등록하는 모달 컴포넌트 (폼 2-A)
 *
 * - 수강권명/Type(P|G)/총 횟수(필수), 사용기한/maxTrainee(선택) 입력
 * - 센터는 부모(현재는 AdminTicketCatalogPage)가 URL의 centerId를 통해 결정해 centerName 전달
 * - `useCreateTicketCatalog` 훅으로 Cloud Function `createTicketCatalog` 호출
 * - 성공 시 onCreated 콜백 → 부모가 모달 close + 목록 refetch
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { useCreateTicketCatalog } from "@/hooks/useTicketCatalog";
import type { CreateTicketCatalogParams } from "@/hooks/useTicketCatalog";

interface CreateTicketCatalogModalProps {
  isOpen: boolean;
  /** 현재 페이지에서 결정된 센터 이름. 백엔드 lookupCenterByName 입력으로 사용. */
  centerName: string;
  onClose: () => void;
  onCreated: () => void;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) {
      return msg;
    }
  }
  return null;
}

/** 빈 문자열은 undefined, 그 외는 parseInt 결과. NaN/소수는 null 반환(검증에서 걸림). */
function parseOptionalPositiveInt(raw: string): number | undefined | null {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n > 0 ? n : null;
}

export function CreateTicketCatalogModal({
  isOpen,
  centerName,
  onClose,
  onCreated,
}: CreateTicketCatalogModalProps) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"P" | "G">("P");
  const [totalCouponInput, setTotalCouponInput] = useState("");
  const [validDaysInput, setValidDaysInput] = useState("");
  const [maxTraineeInput, setMaxTraineeInput] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const createTicketCatalog = useCreateTicketCatalog();

  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setType("P");
      setTotalCouponInput("");
      setValidDaysInput("");
      setMaxTraineeInput("");
      setValidationError(null);
      createTicketCatalog.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setValidationError("수강권명을 입력해주세요.");
      return;
    }

    const totalCoupon = parseOptionalPositiveInt(totalCouponInput);
    if (totalCoupon === undefined || totalCoupon === null) {
      setValidationError("총 횟수는 1 이상의 정수로 입력해주세요.");
      return;
    }

    const validDays = parseOptionalPositiveInt(validDaysInput);
    if (validDays === null) {
      setValidationError("사용기한은 1 이상의 정수로 입력해주세요 (또는 빈 칸).");
      return;
    }

    const maxTrainee = parseOptionalPositiveInt(maxTraineeInput);
    if (maxTrainee === null) {
      setValidationError("maxTrainee는 1 이상의 정수로 입력해주세요 (또는 빈 칸).");
      return;
    }

    setValidationError(null);

    const params: CreateTicketCatalogParams = {
      centerName,
      title: trimmedTitle,
      type,
      totalCoupon,
      ...(validDays !== undefined ? { validDays } : {}),
      ...(maxTrainee !== undefined ? { maxTrainee } : {}),
    };

    createTicketCatalog.mutate(params, {
      onSuccess: () => {
        onCreated();
      },
    });
  };

  const isSubmitDisabled =
    !title.trim() || !totalCouponInput.trim() || createTicketCatalog.isPending;
  const serverErrorMessage = createTicketCatalog.isError
    ? getErrorMessage(createTicketCatalog.error)
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">새 수강권 등록</h2>
        <p className="text-sm text-gray-500 mb-4">센터: {centerName}</p>

        <form onSubmit={handleSubmit}>
          {/* 수강권명 (필수) */}
          <div className="mb-4">
            <label htmlFor="ticketTitle" className="block text-sm font-medium text-gray-700 mb-1">
              수강권명 <span className="text-red-500">*</span>
            </label>
            <input
              id="ticketTitle"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 1:1 필라테스 30회권"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Type (필수) */}
          <div className="mb-4">
            <label htmlFor="ticketType" className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              id="ticketType"
              value={type}
              onChange={(e) => setType(e.target.value as "P" | "G")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="P">P (Private)</option>
              <option value="G">G (Group)</option>
            </select>
          </div>

          {/* 총 횟수 (필수) */}
          <div className="mb-4">
            <label
              htmlFor="ticketTotalCoupon"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              총 횟수 <span className="text-red-500">*</span>
            </label>
            <input
              id="ticketTotalCoupon"
              type="number"
              step={1}
              value={totalCouponInput}
              onChange={(e) => setTotalCouponInput(e.target.value)}
              placeholder="예: 30"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 사용기한 (선택) */}
          <div className="mb-4">
            <label
              htmlFor="ticketValidDays"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              사용기한 (일) <span className="text-xs text-gray-400">— 비우면 무제한</span>
            </label>
            <input
              id="ticketValidDays"
              type="number"
              step={1}
              value={validDaysInput}
              onChange={(e) => setValidDaysInput(e.target.value)}
              placeholder="예: 60"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* maxTrainee (선택) */}
          <div className="mb-4">
            <label
              htmlFor="ticketMaxTrainee"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              수업당 최대 인원 <span className="text-xs text-gray-400">— 비우면 미지정</span>
            </label>
            <input
              id="ticketMaxTrainee"
              type="number"
              step={1}
              value={maxTraineeInput}
              onChange={(e) => setMaxTraineeInput(e.target.value)}
              placeholder="예: 4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 클라이언트 검증 에러 */}
          {validationError && <p className="text-sm text-red-600 mb-4">{validationError}</p>}

          {/* 서버 에러 */}
          {createTicketCatalog.isError && !validationError && (
            <p className="text-sm text-red-600 mb-4">
              {serverErrorMessage ?? "수강권 등록에 실패했습니다. 다시 시도해주세요."}
            </p>
          )}

          {/* 버튼 영역 */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createTicketCatalog.isPending ? "등록 중..." : "등록"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
